var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { TransportError } from '../framework/Transport';
function isPresetMuteConfig(cfg) {
    return typeof cfg !== 'string';
}
export const channelConfiguration = [
    { name: 'Automix Output', startNumbering: 1, count: 1, type: 'automixerOutput' },
    { name: 'Input', startNumbering: 1, count: 32, type: 'input' },
    { name: 'Output', startNumbering: 1, count: 32, type: 'output' },
    { name: 'Preset', startNumbering: 1, count: 16, type: 'preset' },
];
export class AlfatronDspBase {
    constructor(_driverType, state, log, framework) {
        this._driverType = _driverType;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // static properties
        // other class properties and constructor
        this.channels = {};
        this.channelPollIndex = 0;
        this.mutePresets = {};
        this.muteStatusChannels = [];
        this.unmutePresets = {};
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.makeDetectedResults();
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const [command, regExp] = this.makeCommandAndRegExp(channel, 'mute', 'get', false);
                const match = yield this.send(command, regExp, tags);
                return match[1] === '\x01';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.mutePresets[key]) {
                    const presetKey = value ? this.mutePresets[key] : this.unmutePresets[key];
                    const presetChannel = this.channels[presetKey];
                    const presetNum = String.fromCharCode((presetChannel === null || presetChannel === void 0 ? void 0 : presetChannel.channelNumber) - 1);
                    if (presetNum === undefined) {
                        throw new Error(`no ${value ? 'mute' : 'unmute'} preset for ${key}`);
                    }
                    yield this.send(`\xb3\x13\x00\x00${presetNum}\x00\x00\x00\x00\x00\x00\x00`, new RegExp(`\xb3\x13[\\s\\S]\xe0${presetNum}\x00\x00\x00\x00\x00\x00\x00`), tags, { sendTimeoutMs: 4000 });
                    return value;
                }
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const [command, regExp] = this.makeCommandAndRegExp(channel, 'mute', 'set', value);
                const match = yield this.send(command, regExp, tags);
                return match[1] === '\x01';
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const [command, regExp] = this.makeCommandAndRegExp(channel, 'volume', 'get', 0);
                const match = yield this.send(command, regExp, tags);
                let volume = (match[1].charCodeAt(1) << 8) + match[1].charCodeAt(0);
                if ((volume & 0x8000) === 0x8000)
                    volume -= 65536; // convert to signed integer
                return volume;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const [command, regExp] = this.makeCommandAndRegExp(channel, 'volume', 'set', value);
                // The Alfatron can take some time (maybe 500 ms) to respond to volume set commands. In
                // order to prevent jerky volume slider behavior, we don't await on the volume send. We
                // assume the device's volume is correctly set by the send command when it finally
                // responds. If for some reason it isn't, then polling will eventualy correct it.
                this.framework.suspendPolling(4000);
                this.send(command, regExp, tags).catch(error => {
                    if (!(error instanceof TransportError)) {
                        this.log.error(error.message);
                    }
                });
                return value;
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 1200, min: -1200, round: 1 };
                return { max: 1200, min: -3800, round: 1 }; // volume
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                controls: ['mute', 'volume'],
                onePerInterval: true,
            },
            {
                intervalMs: 6000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.muteStatusChannels.length > 0) {
                        const channel = this.muteStatusChannels[this.channelPollIndex];
                        if (channel !== undefined) {
                            const [command, regExp] = this.makeCommandAndRegExp(channel, 'mute', 'get', false);
                            const muteStatus = yield this.send(command, regExp, [
                                `get.mute.${channel.channel}`,
                                'poll',
                            ]);
                            this.state.mutes.update(channel.channel, muteStatus[1] === '\x01');
                        }
                        this.channelPollIndex++;
                        if (this.channelPollIndex >= this.muteStatusChannels.length) {
                            this.channelPollIndex = 0;
                        }
                    }
                    else {
                        // default poll to prevent an idle timeout
                        const tags = ['get.mute.input.1', 'poll'];
                        yield this.send(`\xb3\x22\x00\x00\x2b\x01\x02\x00\x00\x00\x00\x00`, // input 1 mute query
                        /\xb3\x22[\s\S]\xe0\x2b\x01\x02\x00\x00\x00[\x01\x00]\x00/, tags);
                    }
                }),
            },
        ];
        for (const cfg of channelConfiguration) {
            for (let i = 0; i < cfg.count; i++) {
                const n = i + cfg.startNumbering;
                const key = `${cfg.type}.${n}`;
                this.channels[key] = {
                    channelNumber: n,
                    channel: key,
                    name: cfg.count === 1 ? cfg.name : `${cfg.name} ${n}`,
                    type: cfg.type,
                };
            }
        }
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.mutePresets = {};
            this.unmutePresets = {};
            this.muteStatusChannels.length = 0;
            this.channelPollIndex = 0;
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.send(`\xb3\x22\x00\x00\x2b\x01\x02\x00\x00\x00\x00\x00`, /\xb3\x22[\s\S]\xe0\x2b\x01\x02\x00[\s\S]{4}/, ['initialize']);
            if (match)
                return 'connected';
            return 'verifyFailed';
        });
    }
    // other class methods
    getKey(config) {
        return isPresetMuteConfig(config) ? config.muteStatus : config;
    }
    makeDetectedResults() {
        return Object.values(this.channels).map(channel => {
            const isPreset = channel.type === 'preset';
            return {
                channel: channel.channel,
                filters: {
                    mics: !isPreset,
                    micsMuteStatus: !isPreset,
                    amplifier: !isPreset,
                    assistiveListeningMute: !isPreset,
                    assistiveListeningMuteStatus: !isPreset,
                    assistiveListeningVolume: !isPreset,
                    audioInputMute: !isPreset,
                    audioInputMuteStatus: !isPreset,
                    audioInputVolume: !isPreset,
                    overflowRoomMute: !isPreset,
                    overflowRoomMuteStatus: !isPreset,
                    overflowRoomVolume: !isPreset,
                    soundReinforcementMute: !isPreset,
                    soundReinforcementMuteStatus: !isPreset,
                    soundReinforcementVolume: !isPreset,
                    telephony: false,
                    receiveGain: false,
                    preset: isPreset,
                },
                name: channel.name,
                type: channel.type,
            };
        });
    }
    makeCommandAndRegExp(channel, cmdType, action, value) {
        // Command Structure for V1 protocol (values in hex):
        //
        // | Header | Get/Set/Preset | Length (not used) | Version | Data               |
        // | ------ | -------------- | ----------------- | ------- | ------------------ |
        // |     b3 |       22/21/13 |                 0 |       0 | depends on command |
        const actionCmd = action === 'set' ? '\x21' : '\x22';
        const chanNum = String.fromCharCode(channel.channelNumber - 1);
        let moduleId = channel.type === 'input' ? '\x2b\x01' : '\x27\x01';
        let paramType = cmdType === 'mute' ? '\x02\x00' : '\x01\x00';
        let cmd = `${moduleId}${paramType}${chanNum}\x00`;
        let command;
        let regExp;
        if (cmdType === 'mute') {
            if (channel.type === 'input' || channel.type === 'output') {
                command = `\xb3${actionCmd}\x00\x00${cmd}${value ? '\x01' : '\x00'}\x00`;
                // note: the + character must be escaped in the regular expresson
                regExp = new RegExp(`\xb3${actionCmd}[\\s\\S]\xe0${Sutl.escapeRegExp(cmd)}([\\s\\S])[\\s\\S]`);
            }
            else if (channel.type === 'automixerOutput') {
                moduleId = '\xa1\x00';
                paramType = '\x01\x00';
                cmd = `${moduleId}${paramType}`;
                command = `\xb3${actionCmd}\x00\x00${cmd}${value ? '\x01' : '\x00'}\x00\x00\x00`;
                regExp = new RegExp(`\xb3${actionCmd}[\\s\\S]\xe0${moduleId}${paramType}([\\s\\S])[\\s\\S]{3}`);
            }
            else {
                throw new Error(`invalid channel type: ${channel.type}`);
            }
        }
        else if (cmdType === 'volume') {
            if (typeof value !== 'number') {
                throw new Error(`${typeof value} value supplied for volume`);
            }
            const valueLow = String.fromCharCode(value & 0x00ff);
            const valueHigh = String.fromCharCode((value >> 8) & 0x00ff);
            if (channel.type === 'input' || channel.type === 'output') {
                command = `\xb3${actionCmd}\x00\x00${cmd}${valueLow}${valueHigh}`;
                // note: the + character must be escaped in the regular expresson
                regExp = new RegExp(`\xb3${actionCmd}[\\s\\S]\xe0${Sutl.escapeRegExp(cmd)}([\\s\\S]{2})`);
            }
            else if (channel.type === 'automixerOutput') {
                moduleId = '\xa1\x00';
                paramType = '\x02\x00';
                cmd = `${moduleId}${paramType}`;
                command = `\xb3${actionCmd}\x00\x00${cmd}${valueLow}${valueHigh}`;
                regExp = new RegExp(`\xb3${actionCmd}[\\s\\S]\xe0${cmd}([\\s\\S]{2})[\\s\\S]{2}`);
            }
            else {
                throw new Error(`invalid channel type: ${channel.type}`);
            }
        }
        else {
            throw new Error(`invalid command type: ${cmdType}`);
        }
        return [command, regExp];
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (isPresetMuteConfig(config)) {
                const muteStatusChannel = this.channels[config.muteStatus];
                const mutePresetChannel = this.channels[config.mutePreset];
                const unmutePresetChannel = this.channels[config.unmutePreset];
                if (muteStatusChannel === undefined ||
                    mutePresetChannel === undefined ||
                    unmutePresetChannel === undefined) {
                    return 'missing';
                }
                if (muteStatusChannel.type === 'preset' ||
                    mutePresetChannel.type !== 'preset' ||
                    unmutePresetChannel.type !== 'preset') {
                    return 'incompatible';
                }
                this.mutePresets[key] = config.mutePreset;
                this.unmutePresets[key] = config.unmutePreset;
                this.muteStatusChannels.push(muteStatusChannel); // add to channels to poll
            }
            else {
                const channel = this.channels[key];
                if (channel === undefined)
                    return 'missing';
                if (channel.type === 'preset')
                    return 'incompatible';
                if (info.type === 'mute') {
                    this.muteStatusChannels.push(channel); // add to channels to mute epoll
                }
            }
            return 'ok';
        });
    }
}
